import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Home, Eye, EyeOff, Mail, Lock, User, Briefcase, Phone } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const Register = ({ setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Role Selection, 2: Form
  const [userRole, setUserRole] = useState(""); // "seller", "buyer", "realtor"
  const [isRealtor, setIsRealtor] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    realtor_license_number: "",
    realtor_brokerage: "",
    realtor_brokerage_phone: "",
    sms_opt_in: false,
    sms_opt_in_transactional: false,
    sms_opt_in_promotional: false
  });

  // Auto-select role if coming from homepage
  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam && ['seller', 'buyer', 'realtor'].includes(roleParam)) {
      setUserRole(roleParam);
      setIsRealtor(roleParam === 'realtor');
      setStep(2); // Skip to form
    }
  }, [searchParams]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data
      const registerData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        is_realtor: isRealtor,
        phone: formData.phone,
        sms_opt_in_transactional: formData.sms_opt_in_transactional,
        sms_opt_in_promotional: formData.sms_opt_in_promotional
      };

      if (isRealtor) {
        registerData.realtor_license_number = formData.realtor_license_number;
        registerData.realtor_brokerage = formData.realtor_brokerage;
        registerData.realtor_brokerage_phone = formData.realtor_brokerage_phone;
      }

      const response = await axios.post(
        `${API}/api/auth/signup`,
        registerData,
        { withCredentials: true }
      );

      if (response.data) {
        // Show verification status message for realtors
        if (response.data.verification_message) {
          if (response.data.license_verification_status === "verified") {
            toast.success(response.data.verification_message);
          } else {
            toast.info(response.data.verification_message, { duration: 8000 });
          }
        } else {
          toast.success("Account created successfully!");
        }
        
        // Auto-login after registration
        const loginResponse = await axios.post(
          `${API}/api/auth/login`,
          { email: formData.email, password: formData.password },
          { withCredentials: true }
        );

        if (loginResponse.data) {
          setUser(loginResponse.data.user || loginResponse.data);
          
          // Smart redirect based on user role
          if (userRole === 'buyer' || userRole === 'realtor') {
            // Check if they came from a specific city search
            const intendedCity = searchParams.get('city');
            if (intendedCity) {
              navigate(`/search?city=${intendedCity}`);
            } else {
              navigate("/search");
            }
          } else {
            // Sellers go to dashboard to create their listing
            navigate("/dashboard");
          }
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      if (error.response?.status === 400) {
        toast.error("Email already registered. Please login instead.");
      } else {
        toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <Button onClick={() => navigate('/')} variant="ghost">
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Registration Flow */}
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-16">
        <Card className="p-4 sm:p-8 shadow-2xl border-2 border-orange-200">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
              {step === 1 ? "I'm here to..." : "Create Your Account"}
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              {step === 1 ? "Choose your path to get started" : `Sign up as ${userRole === 'realtor' ? 'a Licensed Agent' : userRole === 'seller' ? 'a Home Seller' : 'a Home Buyer'}`}
            </p>
          </div>

          {/* Step 1: Role Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <button
                onClick={() => {
                  setUserRole('seller');
                  setIsRealtor(false);
                  setStep(2);
                }}
                className="w-full p-6 border-2 border-orange-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left group"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                    <Home className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">Sell My Home</h3>
                    <p className="text-sm text-gray-600">List your property for sale by owner (FSBO) and save on commissions</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setUserRole('buyer');
                  setIsRealtor(false);
                  setStep(2);
                }}
                className="w-full p-6 border-2 border-blue-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">Buy a Home</h3>
                    <p className="text-sm text-gray-600">Browse FSBO listings and connect directly with sellers</p>
                  </div>
                </div>
              </button>

              {/* Realtor Note - Not for solicitation */}
              <div className="mt-8 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                <h4 className="font-bold text-red-900 mb-2">📢 Licensed Agents:</h4>
                <p className="text-sm text-red-800 mb-2">
                  <strong>This is a FSBO platform.</strong> Agents may NOT solicit our sellers. Solicitation will result in immediate ban.
                </p>
                <p className="text-sm text-red-700">
                  To join our <strong>Preferred Agent Network</strong> and receive seller-initiated requests, visit: 
                  <a href="/agent-application" className="ml-1 underline font-semibold hover:text-red-900">Apply Here</a>
                </p>
              </div>

              <div className="mt-6 text-center">
                <p className="text-gray-600 text-sm">
                  Already have an account?{" "}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-orange-600 hover:text-orange-700 font-semibold"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Registration Form */}
          {step === 2 && (
            <div>
              <button
                onClick={() => setStep(1)}
                className="text-sm text-orange-600 hover:text-orange-700 mb-6 flex items-center font-medium"
              >
                ← {userRole === 'seller' ? "Not selling? Switch to Buyer" : "Want to sell? Switch to Seller"}
              </button>

              {/* Role Badge */}
              <div className="text-center mb-4">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${
                  userRole === 'seller' 
                    ? 'bg-orange-100 text-orange-800 border-2 border-orange-300' 
                    : 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                }`}>
                  {userRole === 'seller' ? '🏠 Registering as a Home Seller' : '🔍 Registering as a Home Buyer'}
                </span>
              </div>

              {/* No Credit Card Badge */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center space-x-2 bg-green-50 border-2 border-green-200 rounded-full px-4 py-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-green-800 font-semibold text-sm">No Credit Card Required</span>
                </div>
              </div>

          <form onSubmit={handleRegister} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Doe"
                  className="pl-10"
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="you@example.com"
                  className="pl-10"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Minimum 8 characters"
                  className="pl-10 pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
            </div>

            {/* Phone Number Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-gray-400 text-xs">(for SMS notifications)</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(555) 555-5555"
                  className="pl-10"
                  data-testid="register-phone"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Optional - Required only if you want SMS notifications</p>
            </div>

            {/* SMS OPT-IN - SEPARATE CONSENT FOR EACH USE CASE (Telnyx Compliant) */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 space-y-4">
              <div className="text-sm font-semibold text-gray-900 mb-2">
                📱 SMS Notifications (Optional)
              </div>
              <p className="text-xs text-gray-600 mb-3">
                Your phone number is <strong>NEVER shared</strong> with third parties. All SMS come directly from Its-Sold.com.
              </p>
              
              {/* Transactional SMS Opt-In */}
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.sms_opt_in_transactional || false}
                    onChange={(e) => setFormData({...formData, sms_opt_in_transactional: e.target.checked})}
                    className="mt-1 mr-3 h-4 w-4"
                    data-testid="sms-opt-in-transactional"
                  />
                  <div className="text-sm">
                    <div className="font-semibold text-gray-900">
                      Transactional Alerts
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      <p>Receive important account notifications:</p>
                      <ul className="list-disc ml-4 mt-1">
                        <li>Lead assignment notifications (for realtors)</li>
                        <li>Listing status confirmations</li>
                        <li>Account security alerts</li>
                      </ul>
                      <p className="mt-1 text-gray-500">Frequency: 1-3 messages/month | Msg & data rates may apply</p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Promotional SMS Opt-In */}
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.sms_opt_in_promotional || false}
                    onChange={(e) => setFormData({...formData, sms_opt_in_promotional: e.target.checked})}
                    className="mt-1 mr-3 h-4 w-4"
                    data-testid="sms-opt-in-promotional"
                  />
                  <div className="text-sm">
                    <div className="font-semibold text-gray-900">
                      Promotional Messages
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      <p>Receive special offers and updates:</p>
                      <ul className="list-disc ml-4 mt-1">
                        <li>Special offers and discounts</li>
                        <li>New feature announcements</li>
                        <li>Platform tips and updates</li>
                      </ul>
                      <p className="mt-1 text-gray-500">Frequency: 1-3 messages/month | Msg & data rates may apply</p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Compliance Info */}
              <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                <p><strong>Reply STOP</strong> to opt-out at any time | <strong>Reply HELP</strong> for assistance</p>
                <p className="mt-1">
                  View our <a href="/sms-terms" target="_blank" className="text-blue-600 hover:underline font-semibold">SMS Terms & Conditions</a>
                </p>
              </div>
            </div>

            {/* Role-Specific Disclosures */}
            
            {/* SELLER: Licensed Agent Disclosure */}
            {userRole === 'seller' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={isRealtor}
                    onChange={(e) => setIsRealtor(e.target.checked)}
                    className="mt-1 mr-3"
                    data-testid="seller-is-agent"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">I am a licensed real estate agent selling my OWN home</span>
                    <p className="text-xs text-gray-600 mt-1">
                      Agents may sell their personal residence only. You may NOT list properties you represent for clients.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* SELLER Agent Fields (if they checked the box) */}
            {userRole === 'seller' && isRealtor && (
              <div className="space-y-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-orange-800 mb-2">
                  <Briefcase className="w-5 h-5" />
                  <span className="font-semibold">Agent Verification Required</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Texas License Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      required={isRealtor}
                      value={formData.realtor_license_number}
                      onChange={(e) => setFormData({...formData, realtor_license_number: e.target.value})}
                      placeholder="TX-123456"
                      className="pl-10"
                      data-testid="seller-license"
                    />
                  </div>
                </div>
                <p className="text-xs text-orange-700">
                  Your license will be verified with TREC. Misrepresentation will result in account termination.
                </p>
              </div>
            )}

            {/* BUYER: Licensed Agent Option */}
            {userRole === 'buyer' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={isRealtor}
                    onChange={(e) => setIsRealtor(e.target.checked)}
                    className="mt-1 mr-3"
                    data-testid="buyer-is-agent"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">I am a licensed real estate agent</span>
                    <p className="text-xs text-gray-600 mt-1">
                      Checking this helps sellers identify professional buyers. See our <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a> for requirements.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Agent Fields (shown for buyers who check the realtor box) */}
            {userRole === 'buyer' && isRealtor && (
              <div className="space-y-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent License Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      required={isRealtor}
                      value={formData.realtor_license_number}
                      onChange={(e) => setFormData({...formData, realtor_license_number: e.target.value})}
                      placeholder="TX-123456"
                      className="pl-10"
                      data-testid="register-license"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brokerage Name <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <Input
                    type="text"
                    required={false}
                    value={formData.realtor_brokerage}
                    onChange={(e) => setFormData({...formData, realtor_brokerage: e.target.value})}
                    placeholder="Your Brokerage Name"
                    data-testid="register-brokerage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brokerage Phone Number <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <Input
                    type="tel"
                    required={false}
                    value={formData.realtor_brokerage_phone}
                    onChange={(e) => setFormData({...formData, realtor_brokerage_phone: e.target.value})}
                    placeholder="(214) 555-1234"
                    data-testid="register-brokerage-phone"
                  />
                </div>

                <p className="text-xs text-blue-700">
                  Your license will be verified per our <a href="/terms" className="underline hover:text-blue-800">Terms of Service</a>.
                </p>
              </div>
            )}

            {/* SELLER: FSBO Disclaimer & Protection Notice */}
            {userRole === 'seller' && (
              <div className="space-y-4">
                {/* FSBO Platform Notice */}
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-900 mb-1">For Sale By Owner Platform</h4>
                      <p className="text-sm text-amber-800">
                        <strong>Important:</strong> You may only list your <strong>own personal residence</strong> on this platform. 
                        Licensed agents may NOT list properties they represent for clients. This is a For Sale By Owner (FSBO) marketplace.
                      </p>
                      <p className="text-xs text-amber-700 mt-2">
                        By creating an account, you confirm you are the homeowner and not listing on behalf of clients.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Seller Protection Notice */}
                <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-900 mb-1">🛡️ Seller Protection</h4>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li>✓ <strong>No agent solicitation</strong> - We actively block & ban soliciting agents</li>
                        <li>✓ <strong>Verified buyers only</strong> - All buyers must register with verified email</li>
                        <li>✓ <strong>Agent verification</strong> - Licensed agents must provide TX license # for verification</li>
                        <li>✓ <strong>Spam protection</strong> - Automated systems filter spam inquiries</li>
                      </ul>
                      <p className="text-xs text-green-700 mt-2">
                        Since 1998, we've protected FSBO sellers from unwanted solicitation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* REALTOR ROLE: Full professional signup */}
            {userRole === 'realtor' && (
              <div className="space-y-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent License Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      required={true}
                      value={formData.realtor_license_number}
                      onChange={(e) => setFormData({...formData, realtor_license_number: e.target.value})}
                      placeholder="TX-123456"
                      className="pl-10"
                      data-testid="register-license"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brokerage Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    required={true}
                    value={formData.realtor_brokerage}
                    onChange={(e) => setFormData({...formData, realtor_brokerage: e.target.value})}
                    placeholder="Your Brokerage Name"
                    data-testid="register-brokerage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brokerage Phone Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    required={true}
                    value={formData.realtor_brokerage_phone}
                    onChange={(e) => setFormData({...formData, realtor_brokerage_phone: e.target.value})}
                    placeholder="(214) 555-1234"
                    data-testid="register-brokerage-phone"
                  />
                </div>

                <p className="text-xs text-purple-700">
                  Your license will be verified per our <a href="/terms" className="underline hover:text-purple-800">Terms of Service</a>. 
                  Licensed agents can browse FSBO listings and connect with sellers.
                </p>
              </div>
            )}

            <div className="text-xs text-gray-600">
              By signing up, you agree to our{" "}
              <button
                type="button"
                onClick={() => navigate('/terms')}
                className="text-blue-600 hover:underline"
              >
                Terms of Service
              </button>
              {" "}and{" "}
              <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline">
                Privacy Policy
              </a>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-lg py-6"
              data-testid="register-submit"
            >
              {loading ? "Creating account..." : "Create Free Account"}
            </Button>
            
            {/* Already have an account? Login link */}
            <div className="text-center text-sm text-gray-600 mt-4">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-orange-600 hover:text-orange-700 font-semibold hover:underline"
              >
                Login
              </button>
            </div>
              </form>
            </div>
          )}
        </Card>

        {/* Value Props */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center text-sm">
          <div>
            <div className="text-3xl mb-1">🏠</div>
            <p className="text-gray-700 font-semibold">Free Forever</p>
          </div>
          <div>
            <div className="text-3xl mb-1">🤖</div>
            <p className="text-gray-700 font-semibold">AI-Powered</p>
          </div>
          <div>
            <div className="text-3xl mb-1">💰</div>
            <p className="text-gray-700 font-semibold">Save $15K+</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
